/* eslint-disable */
import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Form, Input, Radio, Spin, TreeSelect } from 'antd'
import { fetchEnumOptions } from '@/redux/components/enumOptionsSlice'
import { fetchSystemPermissionsList } from '@/redux/use/systemPermissionSlice'
import {
  buildPermissionTreeSelectData,
  collectPermissionDescendantIds,
  getPermissionParentCodePrefix
} from '@/utils/rolePermissionTree'

const { TextArea } = Input

/** 权限 record → 表单项（不能 initialValues={record}：含 id、roles 等与 Form.Item name 不一致） */
function initialValuesFromPermissionProps(actType, initialRecord, defaultParentId, permissionsList) {
  const empty = {
    parentId: undefined,
    type: '',
    name: '',
    code: '',
    remark: ''
  }
  if (initialRecord) {
    const pid = initialRecord.parentId
    const parentVal =
      pid != null && pid !== '' && String(pid) !== '0' ? String(pid) : undefined
    let nameVal = initialRecord.name ?? ''
    let codeVal = initialRecord.code ?? ''
    if (parentVal) {
      const codePrefix = getPermissionParentCodePrefix(permissionsList, parentVal)
      if (codePrefix && codeVal.startsWith(codePrefix)) {
        codeVal = codeVal.slice(codePrefix.length)
      }
    }
    return {
      parentId: parentVal,
      value:
        initialRecord.type != null && initialRecord.type !== ''
          ? String(initialRecord.type.value)
          : '',
      name: nameVal,
      code: codeVal,
      remark: initialRecord.remark ?? ''
    }
  }
  if (actType === 'create' && defaultParentId != null && defaultParentId !== '') {
    return { ...empty, parentId: String(defaultParentId) }
  }
  return empty
}

function applyDisabledToTreeData(items, disabledSet) {
  if (!Array.isArray(items)) return []
  return items.map((node) => ({
    ...node,
    disabled: disabledSet.has(String(node.value)),
    children: node.children ? applyDisabledToTreeData(node.children, disabledSet) : undefined
  }))
}

const mapStateToProps = (state) => ({
  permissionsList: state.use.systemPermission.list,
  permissionsFetching: state.use.systemPermission.fetching,
  enumOptions: state.components.enumOptions
})

const mapDispatchToProps = {
  fetchSystemPermissionsList,
  fetchEnumOptions
}

class PermissionForm extends React.Component {
  static propTypes = {
    form: PropTypes.object,
    permissionsList: PropTypes.array,
    permissionsFetching: PropTypes.bool,
    enumOptions: PropTypes.object,
    fetchSystemPermissionsList: PropTypes.func,
    fetchEnumOptions: PropTypes.func,
    actType: PropTypes.oneOf(['create', 'edit']),
    initialRecord: PropTypes.object,
    defaultParentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }

  static defaultProps = {
    actType: 'create'
  }

  componentDidMount() {
    const { permissionsList, enumOptions } = this.props
    if (!permissionsList || permissionsList.length === 0) {
      this.props.fetchSystemPermissionsList()
    }
    const pt = enumOptions.PermissionType
    if ((!pt.data || pt.data.length === 0) && !pt.fetching) {
      this.props.fetchEnumOptions({ key: 'PermissionType', dataType: 'option' })
    }
  }

  componentDidUpdate(prevProps) {
    const { form, permissionsList } = this.props
    if (!form || !form.current) return
    const sig = permissionsList?.length ?? 0
    const prevSig = prevProps.permissionsList?.length ?? 0
    if (sig !== prevSig) {
      form.current.setFieldsValue({ __permListSig: sig })
    }
  }

  getParentTreeData = () => {
    const { permissionsList, actType, initialRecord } = this.props
    const flat = permissionsList || []
    const base = buildPermissionTreeSelectData(flat, { maxLevels: 2 })
    if (actType !== 'edit' || !initialRecord?.id) {
      return base
    }
    const forbidden = collectPermissionDescendantIds(flat, initialRecord.id)
    const disabledSet = new Set(forbidden)
    disabledSet.add(String(initialRecord.id))
    return applyDisabledToTreeData(base, disabledSet)
  }

  render() {
    const { form, permissionsFetching, enumOptions, actType, initialRecord, defaultParentId } = this.props
    const treeData = this.getParentTreeData()
    const { PermissionType: permissionTypeBucket } = enumOptions
    const permissionTypeOptions = (permissionTypeBucket.data || []).map((o) => ({
      value: o.id,
      label: o.value
    }))
    const listSig = (this.props.permissionsList && this.props.permissionsList.length) || 0
    const initialValues = {
      ...initialValuesFromPermissionProps(
        actType,
        initialRecord,
        defaultParentId,
        this.props.permissionsList
      ),
      __permListSig: listSig
    }
    // 换一条编辑记录 / 换新建场景时须 remount；列表加载后重算编码去前缀（依赖父级 code）
    const formMountKey =
      actType === 'edit' && initialRecord?.id != null
        ? `edit-${initialRecord.id}-${listSig}`
        : `create-${defaultParentId ?? 'root'}-${listSig}`

    return (
      <Form
        key={formMountKey}
        ref={form}
        layout="vertical"
        style={{ maxWidth: 560 }}
        initialValues={initialValues}
      >
        <Form.Item
          name="parentId"
          label="父级权限"
          tooltip="仅展示两层（顶级与其子级）；不选表示顶级；编辑时不可将父级设为自己或自己的子节点"
        >
          <TreeSelect
            allowClear
            showSearch
            treeNodeFilterProp="title"
            loading={permissionsFetching}
            placeholder="不选则为顶级"
            treeData={treeData}
            treeDefaultExpandAll
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item name="value" label="类型">
          {permissionTypeBucket.fetching && permissionTypeOptions.length === 0 ? (
            <Spin />
          ) : (
            <Radio.Group optionType="button" buttonStyle="solid">
              {permissionTypeOptions.map((o) => (
                <Radio.Button key={o.value} value={o.value}>
                  {o.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          )}
        </Form.Item>
        <Form.Item
          name="name"
          label="权限名称"
          rules={[{ required: true, message: '请填写权限名称' }]}
        >
          <Input placeholder="展示名称" />
        </Form.Item>
        <Form.Item name="__permListSig" hidden noStyle>
          <input type="hidden" />
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.parentId !== cur.parentId || prev.__permListSig !== cur.__permListSig}>
          {({ getFieldValue }) => {
            const pid = getFieldValue('parentId')
            const codePrefix = getPermissionParentCodePrefix(this.props.permissionsList, pid)
            return (
              <Form.Item
                name="code"
                label="权限编码"
                rules={[{ required: true, message: '请填写权限编码' }]}
                tooltip={codePrefix ? '前半段为父级权限编码 + 「:」，只需填写后半段' : undefined}
              >
                {codePrefix ? (
                  <Input
                    placeholder="后半段，如 user:list"
                    addonBefore={codePrefix}
                    style={{ fontFamily: 'monospace' }}
                  />
                ) : (
                  <Input
                    placeholder="唯一编码，如 system:user:list"
                    style={{ fontFamily: 'monospace' }}
                  />
                )}
              </Form.Item>
            )
          }}
        </Form.Item>
        <Form.Item name="remark" label="备注">
          <TextArea rows={3} placeholder="选填" allowClear />
        </Form.Item>
      </Form>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(PermissionForm)
